import { useState, useEffect } from 'react';
import { Outlet, useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { MobileLayout } from './mobile/MobileLayout';
import { isNative } from '../../lib/platform';
import { useStore } from '../../contexts/StoreContext';
import { useAuth } from '../../contexts/AuthContext';
import { storesApi } from '../../api/endpoints/stores';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { ZeroChat } from '../ui/ZeroChat';
import { ProductFormProvider } from '../../contexts/ProductFormContext';
import { CouponFormProvider } from '../../contexts/CouponFormContext';
import { PaylinkFormProvider } from '../../contexts/PaylinkFormContext';
import { ImagePickerProvider } from '../../contexts/ImagePickerContext';
import { openAuthPrompt } from '../../lib/authPrompt';
import { QuestionnaireModal } from '../questionnaires/QuestionnaireModal';

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { storeId } = useParams();
  const { currentStore, setCurrentStore } = useStore();
  const { user, isDemo, isLoading: authLoading } = useAuth();

  // Fetch stores (demo mode gets mocked data via axios interceptor).
  const { data, isLoading, isError } = useQuery({
    queryKey: ['stores'],
    queryFn: storesApi.list,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: !!user || isDemo,
  });

  useEffect(() => {
    const stores = data?.stores;
    if (stores && storeId) {
      const store = stores.find((s) =>
        s.id === storeId || s._id === storeId || s._id?.toString?.() === storeId,
      );
      if (store && (store.id !== currentStore?.id || !currentStore?.role)) {
        setCurrentStore(store);
      }
    }
  }, [data, storeId, currentStore?.id, currentStore?.role, setCurrentStore]);

  // Not signed in - auth interceptor already opened the modal. Show a blurred shell.
  if (!authLoading && !user && !isDemo) {
    return <UnauthenticatedShell />;
  }

  if (authLoading || isLoading) {
    return <LoadingSpinner fullPage size="lg" />;
  }

  // Authenticated but the store isn't in the user's list - fake / revoked id.
  const stores = data?.stores ?? [];
  const matched = storeId && stores.find((s) =>
    s.id === storeId || s._id === storeId || s._id?.toString?.() === storeId,
  );
  if (!isDemo && !isError && stores.length > 0 && !matched) {
    return <StoreNotAccessible storeId={storeId || ''} />;
  }

  // Capacitor builds get the iOS/Android tab-bar shell. The web app keeps the
  // existing sidebar + topbar so desktop merchants see no change.
  const Shell = isNative ? (
    <MobileLayout />
  ) : (
    <div className="flex h-screen overflow-hidden bg-bg-tertiary">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 2xl:p-10 bg-bg-primary">
          <div className="mx-auto max-w-6xl 2xl:max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
      <ZeroChat />
      <QuestionnaireModal />
    </div>
  );

  return (
    <ProductFormProvider>
      <CouponFormProvider>
        <PaylinkFormProvider>
          <ImagePickerProvider>
            {Shell}
          </ImagePickerProvider>
        </PaylinkFormProvider>
      </CouponFormProvider>
    </ProductFormProvider>
  );
}

function UnauthenticatedShell() {
  return (
    <div
      className="flex h-screen items-center justify-center bg-bg-tertiary"
      style={{ filter: 'blur(2px)' }}
      aria-hidden
    >
      <div className="text-center text-text-muted">
        <p className="text-sm">...</p>
      </div>
    </div>
  );
}

function StoreNotAccessible({ storeId }: { storeId: string }) {
  return (
    <div className="flex h-screen items-center justify-center bg-bg-tertiary p-6">
      <div className="max-w-md w-full text-center ui-card p-8" dir="rtl">
        <div
          className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
          style={{ background: 'rgba(210,233,254,0.12)', color: '#D2E9FE' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="13" />
            <line x1="12" y1="16" x2="12" y2="16" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-text-primary mb-2">المتجر غير متاح</h2>
        <p className="text-sm text-text-muted mb-6">
          لا نجد متجراً بالمعرف <span className="mono num" dir="ltr">{storeId}</span> أو لا تملك صلاحية الوصول إليه.
        </p>
        <div className="flex flex-col gap-2">
          <Link
            to="/"
            className="block w-full rounded-xl px-4 py-3 text-[13px] font-semibold"
            style={{ background: '#D2E9FE', color: '#0A2248' }}
          >
            العودة إلى قائمة متاجري
          </Link>
          <button
            type="button"
            onClick={() => openAuthPrompt('signin')}
            className="block w-full rounded-xl px-4 py-3 text-[13px] font-semibold text-text-secondary hover:text-text-primary transition-colors"
            style={{ border: '1px solid var(--color-border-default)' }}
          >
            تسجيل الدخول بحساب آخر
          </button>
        </div>
      </div>
    </div>
  );
}
